import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import BlankProfile from '../../../assets/images/blank-profile.png';
import { MainHead } from '../../../components/common/head/MainHead';
import { usePutUsersMeMutation } from '../../../hooks/api/user/mutations/usePutUsersMe.mutation';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useGetUserProfile } from '../../../hooks/user/useGetUserProfile';
import { BasicInput } from '../../../components/common/inputs/BasicInput';
import { usePostImageUserProfileMutation } from '../../../hooks/api/etc/upload/mutations/usePostImageUserProfile.mutation';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useNotification } from '../../../provider/ToastNotificationProvider';
import { SpinnerLoader } from '../../../components/common/loading/SpinnerLoader';

export const MyInfoEditProfilePage = () => {
  const navigate = useNavigate();
  const [nickname, setNickname] = useState<string>();
  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  const { userProfile } = useGetUserProfile();
  const { mutate: putUsersMe, isLoading: isUserProfileUpdating } = usePutUsersMeMutation();
  const { mutate: postImageUserProfileMutation, isLoading: isFileUploading } = usePostImageUserProfileMutation();
  const { openNotification } = useNotification();

  const isLoading = isFileUploading || isUserProfileUpdating;
  const isSubmitDisabled = !nickname || nickname.length <= 0 || isLoading;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
      setProfileFile(file);
    }
  };

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: true }} headerTitle={'회원 정보 수정'} />,
    fixedHeaderHeight: 48,
    fixedFooter: (
      <ButtonSection>
        <BlurredSection />
        <SubmitButton
          type={'button'}
          disabled={isSubmitDisabled}
          onClick={() => {
            handleConfirmButton();
          }}
        >
          <SpinnerLoader isVisible={isLoading ?? false} />
          {!isLoading && <SubmitButtonText>{'완료'}</SubmitButtonText>}
        </SubmitButton>
      </ButtonSection>
    ),
    fixedFooterHeight: 72,
    style: { background: '#fff' },
  });

  const handleConfirmButton = () => {
    if (profileFile) {
      postImageUserProfileMutation(
        {
          reqBody: {
            file: profileFile,
          },
        },
        {
          onSuccess: (res: { url: string }) => {
            putUser(res.url);
          },
        },
      );
      return;
    }
    putUser();
  };

  const putUser = (newProfileImageUrl?: string) => {
    const reqBody = newProfileImageUrl ? { nickname, profileImage: newProfileImageUrl } : { nickname };
    return putUsersMe(
      {
        reqBody,
      },
      {
        onSuccess: () => {
          navigate(pageInfo.myInfoPage.path);
          openNotification({
            message: '저장이 완료 되었습니다',
          });
        },
      },
    );
  };

  useEffect(() => {
    if (userProfile && userProfile.nickname) {
      setNickname(userProfile.nickname);
    }
  }, [userProfile]);

  return (
    <Container>
      <ProfileCard>
        <ImageWrap>
          {previewSrc ? (
            <ProfileImage src={previewSrc} />
          ) : (
            <ProfileImage src={userProfile?.profileImage ? userProfile.profileImage : BlankProfile} />
          )}
        </ImageWrap>
        <Nickname className="sensitive-text">{userProfile?.nickname}</Nickname>
        <EditProfileImageSection>
          <EditProfileImageButton onClick={handleButtonClick}>{'프로필 사진 수정'}</EditProfileImageButton>
          <input
            type={'file'}
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept={'image/*'}
            onChange={handleFileChange}
          />
        </EditProfileImageSection>
      </ProfileCard>
      <InputSection>
        <BasicInputWithLabelWrapper>
          <Label>{'닉네임'}</Label>
          <BasicInput
            type={'text'}
            value={nickname}
            placeholder={''}
            onChange={(event) => {
              if (event.target.value) {
                const newValue = String(event.target.value);
                setNickname(newValue);
              } else {
                setNickname(undefined);
              }
            }}
          />
        </BasicInputWithLabelWrapper>
      </InputSection>
      <InputSection>
        <BasicInputWithLabelWrapper>
          <Label>{'휴대폰 번호'}</Label>
          <BasicInput type={'tel'} value={userProfile?.phoneNumber} placeholder={''} disabled />
        </BasicInputWithLabelWrapper>
      </InputSection>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;
`;

const ProfileCard = styled.div`
  width: calc(100%);

  padding: 16px;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  background-color: #ffffff;

  gap: 20px;

  border-radius: 16px;
`;

const Nickname = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* System/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 13.6px */
`;

const EditProfileImageSection = styled.div``;

const EditProfileImageButton = styled.button`
  display: flex;
  height: 28px;
  padding: 8px;
  align-items: center;
  gap: 4px;

  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const InputSection = styled.div`
  display: flex;
  padding: 0px var(--radius-large, 16px) 32px var(--radius-large, 16px);
  flex-direction: column;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const BasicInputWithLabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-medium, 8px);
  align-self: stretch;
`;

const ProfileImage = styled.img`
  width: 66px;
  height: 66px;

  border-radius: 32px;
`;

const Label = styled.label`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* System/Small/s-regular */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 10.2px */
`;

const Input = styled.input`
  width: 200px;
  height: 40px;

  padding: 0px 16px;

  border: none;

  border-radius: 4px;
  border-bottom: 1px solid #efeff1;

  font-size: 14px;
  line-height: 14px;
`;

const ImageWrap = styled.span`
  display: flex;
  padding: 0px 24px;
  flex-direction: column;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const ButtonSection = styled.div`
  position: relative;
  display: flex;
  width: 100%;

  padding: 0px 16px 32px 16px;
  background-color: white;

  justify-content: center;
  align-items: center;

  gap: 8px;
`;

const BlurredSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: var(--radius-large, 16px);
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  position: absolute;
  top: -40px;
`;

const Button = styled.button`
  width: 100%;
  height: 40px;
  max-height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 16px;

  gap: 4px;

  border-radius: var(--radius-medium, 8px);

  &:disabled {
    opacity: 0.3;
  }

  cursor: pointer;
`;

const ButtonText = styled.span`
  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const SubmitButton = styled(Button)`
  background: var(--Light-Scheme-Color-primary-blue, #445eff);
`;

const SubmitButtonText = styled(ButtonText)`
  color: var(--Light-Scheme-Monochrome-white, #fff);
`;
